import { format } from 'date-fns';
import { ref, update } from 'firebase/database';
import moment from 'moment/moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Input } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';

import checkSvg from '../../../assets/img/check_icon.svg';
import { database } from '../../../Common/Firebase';
import { getUser, getUserType } from '../../../redux/slices/userSlice';
import { ICL, TaskKeysType } from '../../../shared/types';

interface Props {
  id: string;
  completedAt?: string;
  checkedAt?: string;
  handleChangeTaskByKey: (id: string, key: TaskKeysType, value: string | boolean | string[]) => void;
  icl: ICL | null;
  textTask: string;
  hw?: boolean;
}

export default function CompleteCheckTask({
  id,
  completedAt,
  checkedAt,
  handleChangeTaskByKey,
  icl,
  textTask,
  hw,
}: Props) {
  const { t } = useTranslation();
  const userTypeSelector = useSelector(getUserType);
  const user = useSelector(getUser);

  const sendCheckNotification = (email: string, icl: ICL) => {
    const now = new Date();
    const formattedTime = format(now, 'dd MM yyyy HH:mm');
    const notificationId = uuidv4();

    const notificationData = {
      id: notificationId + email,
      idItem: email,
      subject: icl.subject,
      name: user.FIO,
      taskText: textTask,
      time: formattedTime,
      type: hw ? 'taskCheckedHw' : 'taskChecked',
      iclId: icl.id,
      workType: icl.type,
      viewed: true,
    };

    update(ref(database, `Students/${email}/notification`), {
      [notificationId + email]: notificationData,
    });
  };

  const sendCompleteNotification = (email: string, icl: ICL, complete: boolean) => {
    const now = new Date();
    const formattedTime = format(now, 'dd MM yyyy HH:mm');
    const notificationId = uuidv4();

    const notificationData = {
      id: notificationId + email,
      idItem: email,
      subject: icl.subject,
      name: user.FIO,
      taskText: textTask,
      time: formattedTime,
      type: hw ? 'taskCompletedHw' : 'taskCompleted',
      iclId: icl.id,
      taskId: id,
      workType: icl.type,
      viewed: true,
      complete,
    };

    update(ref(database, `Teachers/${email}/notification`), {
      [notificationId + email]: notificationData,
    });
  };

  const handleCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChangeTaskByKey(id, 'checkedAt', event.target.checked ? moment().format('DD.MM') : '');

    if (event.target.checked && icl && icl?.studentEmail) {
      sendCheckNotification(icl.studentEmail, icl);
    }
  };

  const handleComplete = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChangeTaskByKey(id, 'completedAt', event.target.checked ? moment().format('DD.MM') : '');

    if (icl?.teacherEmail) {
      sendCompleteNotification(icl?.teacherEmail, icl, event.target.checked);
    }
  };

  return (
    <div className="mt-[32px] flex flex-1 flex-row gap-[24px] px-[16px] py-[12px]" style={{ maxWidth: 300 }}>
      <div className="w-1/2 cursor-pointer pb-[8px]">
        <div className="mb-[5px] flex text-[12px] uppercase tracking-widest text-[#656D7B]">
          {completedAt && userTypeSelector === 'Teacher' ? <img src={checkSvg} alt={'check'} /> : null}
          {completedAt ? t('completed') : t('not completed')}
        </div>

        <div className="flex items-center">
          {userTypeSelector === 'Student' && (
            <Input
              type="checkbox"
              className="mr-[12px] mt-0 h-[20px] w-[20px] cursor-pointer border-2 border-[#FA6238] checked:border-[#08B781] checked:bg-[#08B781] checked:ring-[#08B781] checked:ring-opacity-50 focus:border-[#08B781] focus:ring-[#08B781] focus:ring-opacity-50"
              checked={Boolean(completedAt)}
              onChange={e => handleComplete(e)}
            />
          )}

          <div className={`text-[20px] ${completedAt ? 'text-black' : 'text-[#656D7B]'} leading-6`}>
            {completedAt ? completedAt : '00.00'}
          </div>
        </div>
      </div>

      <div className="w-1/2 cursor-pointer  pb-[8px]">
        <div className="mb-[5px] flex text-[12px] uppercase tracking-widest text-[#656D7B]">
          {checkedAt && userTypeSelector === 'Student' ? <img src={checkSvg} alt={'check'} /> : null}
          {checkedAt ? t('checked') : t('no checked')}
        </div>

        <div className="flex items-center">
          {userTypeSelector === 'Teacher' && (
            <Input
              type="checkbox"
              className="mr-[12px] mt-0 h-[20px] w-[20px] cursor-pointer border-2 border-[#FA6238] checked:border-[#08B781] checked:bg-[#08B781] checked:ring-[#08B781] checked:ring-opacity-50 focus:border-[#08B781] focus:ring-[#08B781] focus:ring-opacity-50"
              checked={Boolean(checkedAt)}
              onChange={e => handleCheck(e)}
            />
          )}

          <div className={`text-[20px] ${checkedAt ? 'text-black' : 'text-[#656D7B]'} leading-6`}>
            {checkedAt ? checkedAt : '00.00'}
          </div>
        </div>
      </div>
    </div>
  );
}
